<template>
  <div class="file-search-preview file-search-preview__root">
    <template v-if="hasResultsError">
      <pui-flex direction="row" justifyContent="center" class="file-search-preview__error">
        <i class="mdi mdi-close-circle-outline" />
        <pui-flex direction="column" alignItems="center">
          <div class="file-search-preview__error-primary">
            {{ $t('dam.search.preview.error.primary') }}
          </div>
          <div @click.stop="getSearchResults" class="file-search-preview__error-reload">
            {{ $t('dam.search.preview.error.reload') }}
          </div>
        </pui-flex>
      </pui-flex>
    </template>

    <template v-else-if="loadingResults">
      <pui-common-spinner class="file-search-preview__spinner" />
    </template>

    <template v-else>
      <pui-flex class="file-search-preview__main">
        <pui-column-container
          v-if="results.length"
          :items="filesToDisplay"
          :getItemId="getFileId"
          :cols="cols"
          :minItemWidth="100"
          :wrap="false"
          @resize="onColumnContainerResize"
          class="file-search-preview__column-container"
        >
          <template #default="{ item: file }">
            <pui-aspect-ratio
              @click="$emit('searchForFile', file)"
              class="file-search-preview__file"
            >
              <div class="file-search-preview__file-inside">
                <pui-dam-file-viewer
                  :file="file"
                  class="file-search-preview__file-thumbnail"
                  objectFit="contain"
                />
                <div class="file-search-preview__file-meta">{{ file.name }}</div>
              </div>
            </pui-aspect-ratio>
          </template>
        </pui-column-container>

        <pui-flex
          v-if="results.length"
          @click.stop="$emit('executeSearch')"
          class="file-search-preview__total-count pui-link"
          direction="column"
          justifyContent="center"
        >
          {{ $t('dam.search.preview.total_count', { count: totalNumberOfItems }) }}
        </pui-flex>
        <pui-flex
          v-else
          class="file-search-preview__no_results"
          direction="row"
          justifyContent="center"
          flex="1"
        >
          {{ $t('dam.search.preview.no_results') }}
        </pui-flex>
      </pui-flex>
    </template>
  </div>
</template>

<script>
import Vue from 'vue';

import puiDamFileViewer from '../DamFileViewer';
import { getDamService } from '../../../services';

const FILE_SERVICE_NAMESPACE = 'search-preview';

const MAX_FILE_COUNT = 10;

const INITIAL_FILE_COL_COUNT = 9;

export default {
  components: {
    puiDamFileViewer,
  },
  props: {
    /**
     * Generates the search query for the param search groups,
     * with folder selection built in.
     */
    generateSearchQuery: {
      type: Function,
      required: true,
    },
    /**
     * Additional search filters
     */
    searchItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      // Files
      results: [],
      loadingResults: false,
      hasResultsError: false,
      totalNumberOfItems: 0,
      // Columns
      cols: INITIAL_FILE_COL_COUNT,
    };
  },
  computed: {
    /**
     * @returns {object[]} the actual array of files to display
     * for the responsive preview
     */
    filesToDisplay() {
      return this.results.slice(0, this.cols);
    },
  },
  methods: {
    // SEARCH
    /**
     * Loads the search results
     *
     * @returns {Promise<void>}
     */
    async getSearchResults() {
      try {
        // Reset items and do nothing if there are no search items
        if (!this.searchItems.length) {
          this.results = [];
          this.loadingResults = false;
          this.hasResultsError = false;

          return;
        }

        this.loadingResults = true;
        this.hasResultsError = false;

        const globalSearchQuery = this.generateSearchQuery([this.searchItems]);

        const searchConfig = {
          top: MAX_FILE_COUNT,
          skip: 0,
          filter: globalSearchQuery,
          attributes: '',
        };

        const response = await getDamService('filesService').search(
          FILE_SERVICE_NAMESPACE,
          searchConfig
        );

        const { items, totalNumberOfItems } = response.body;

        this.results = items;
        this.totalNumberOfItems = totalNumberOfItems;
        this.$emit('refreshedPreview');
      } catch (err) {
        if (!Vue.http.isCancel(err)) {
          this.hasResultsError = true;
        }
      }

      this.loadingResults = false;
    },
    // FILES
    /**
     * Triggered when the column container resizes
     *
     * @param {number} suggestedOffset - the suggested offset to the
     * current number of columns, for the items to maintain their size
     * @returns {void}
     */
    onColumnContainerResize(suggestedOffset) {
      const nextColCount = INITIAL_FILE_COL_COUNT + suggestedOffset;

      this.cols = Math.max(Math.min(nextColCount, MAX_FILE_COUNT), 0);
    },
    /**
     * @param {object} file - a file result
     * @param {number} fileIndex - the index of the file in the results
     * @returns {string} a unique id for the file
     */
    getFileId(file, fileIndex) {
      return file.itemId;
    },
    // EXPOSED
    /**
     * Refreshes the search preview results
     */
    refresh() {
      this.getSearchResults();
    },
  },
  /**
   * Mounted hook
   *
   * @returns {void}
   */
  mounted() {
    this.getSearchResults();
  },
};
</script>
